import { CopilotSessionBatchStatusEnum, CopilotToolEnum, CopilotToolStateEnum } from "~/utils/enums/copilot/chat.enums";
import { SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import type {
  IAddProviderToolInput,
  ICopilotToolLabels,
  ICopilotToolSummaryDeps,
  ICopilotToolSummaryLine,
  ICreateSessionBatchOutput,
  ICreateSessionBatchResult,
  ICreateSessionBatchToolInput,
  ICreateSessionDates,
  ICreateSessionRowResult,
  ICreateTrainingToolInput,
  IUpdateTrainingToolInput,
  CreateSessionToolInput,
} from "~/utils/interfaces/copilot-interfaces";

/**
 * Only the approval-gated tools get an entry: the read tools (searchCatalog, analyzeDocument…)
 * also stream as `tool-*` parts and must keep rendering nothing. `reviewTraining` runs without
 * approval and hands over to the training form — its output is rendered by `CopilotReviewCard`.
 */
export const COPILOT_TOOL_LABELS: Partial<Record<CopilotToolEnum, ICopilotToolLabels>> = {
  [CopilotToolEnum.CREATE_TRAINING]: {
    title: "Créer la formation dans le catalogue",
    icon: "graduation_cap",
    confirm: "Créer la formation",
  },
  [CopilotToolEnum.UPDATE_TRAINING]: {
    title: "Mettre à jour la formation dans le catalogue",
    icon: "edit",
    confirm: "Mettre à jour la formation",
  },
  [CopilotToolEnum.ADD_PROVIDER]: {
    title: "Ajouter le prestataire au catalogue",
    icon: "briefcase",
    confirm: "Ajouter le prestataire",
  },
  [CopilotToolEnum.CREATE_SESSION]: {
    title: "Créer la session de formation",
    icon: "calendar",
    confirm: "Créer la session",
  },
};

/** How the catalog offer created with the training reads in the app's own wording. */
export const SESSION_ITEM_TYPE_LABELS: Record<SessionItemTypeEnum, string> = {
  [SessionItemTypeEnum.GROUP]: "Groupe (intra)",
  [SessionItemTypeEnum.INDIVIDUAL]: "Individualisée (inter)",
};

/**
 * The action object of a tool the front does not gate has no card to draw. The session card counts
 * the periods it covers: one approval creates them all, so its wording says how many. Below two the
 * registry's singular card stands — for the legacy shape, a batch of one, and an input still streaming.
 */
export function copilotToolLabelsOf(toolName: CopilotToolEnum, input?: unknown): ICopilotToolLabels | null {
  const labels = COPILOT_TOOL_LABELS[toolName] ?? null;
  if (!labels || toolName !== CopilotToolEnum.CREATE_SESSION) return labels;

  const count = createSessionPeriodsOf(input).length;
  if (count < 2) return labels;

  return {
    ...labels,
    title: `Créer ${count} sessions de formation`,
    confirm: `Créer les ${count} sessions`,
    running: `Création des ${count} sessions en cours…`,
  };
}

/** "14h · 2 jours" — either half alone is a valid duration, neither is no line at all. */
export function copilotToolDurationLabelOf(hours?: number | null, days?: number | null): string | null {
  const parts = [hours ? `${hours}h` : null, days ? `${days} jour${days > 1 ? "s" : ""}` : null].filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

/**
 * One readable line per meaningful input field, so the user approves in full knowledge. A field the
 * model left empty is dropped rather than shown blank — and the raw ids (trainingId, sessionItemId)
 * are never shown: they mean nothing to the user.
 */
export function copilotToolSummaryOf(
  toolName: CopilotToolEnum,
  input: unknown,
  deps: ICopilotToolSummaryDeps,
): ICopilotToolSummaryLine[] {
  if (toolName === CopilotToolEnum.CREATE_TRAINING) {
    const createInput = input as ICreateTrainingToolInput | undefined;

    return [
      { label: "Nom", value: createInput?.title ?? null },
      {
        label: "Prestataire",
        value: createInput?.providerName
          ? `${createInput.providerName}${createInput.isNewProvider ? " (nouveau)" : ""}`
          : null,
      },
      { label: "Durée", value: copilotToolDurationLabelOf(createInput?.durationHours, createInput?.durationDays) },
      { label: "Programme", value: createInput?.program ?? null },
      {
        label: "Inscriptions",
        value: createInput?.sessionItemType ? (SESSION_ITEM_TYPE_LABELS[createInput.sessionItemType] ?? null) : null,
      },
      { label: "Prix HT", value: createInput?.priceExclTax != null ? `${createInput.priceExclTax} €` : null },
      { label: "Participants max", value: createInput?.maxNbAttendees ? `${createInput.maxNbAttendees}` : null },
    ].filter((line) => !!line.value);
  }

  if (toolName === CopilotToolEnum.UPDATE_TRAINING) {
    const updateInput = input as IUpdateTrainingToolInput | undefined;

    return [
      { label: "Nom", value: updateInput?.title ?? null },
      { label: "Durée", value: copilotToolDurationLabelOf(updateInput?.durationHours, updateInput?.durationDays) },
      { label: "Programme", value: updateInput?.program ?? null },
    ].filter((line) => !!line.value);
  }

  if (toolName === CopilotToolEnum.ADD_PROVIDER) {
    const providerInput = input as IAddProviderToolInput | undefined;

    return [{ label: "Prestataire", value: providerInput?.providerName ?? null }].filter((line) => !!line.value);
  }

  if (toolName === CopilotToolEnum.CREATE_SESSION) {
    const sessionInput = input as CreateSessionToolInput | undefined;
    const periods = createSessionPeriodsOf(input);

    return [
      // One period keeps the card's usual "Dates" line; several are numbered in the order proposed.
      ...periods.map((period, index) => ({
        label: periods.length > 1 ? `Session ${index + 1}` : "Dates",
        value: createSessionPeriodLabelOf(period, deps),
      })),
      { label: "Intitulé", value: sessionInput?.designation ?? null },
      { label: "Participants max", value: sessionInput?.maxNbAttendees ? `${sessionInput.maxNbAttendees}` : null },
      { label: "Prix HT", value: sessionInput?.priceExclTax != null ? `${sessionInput.priceExclTax} €` : null },
      { label: "Adresse", value: sessionInput?.address ?? null },
    ].filter((line) => !!line.value);
  }

  return [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** A period is only one with both of its dates: "du 12 mars au undefined" is worse than nothing. */
function createSessionPeriodOf(value: unknown): ICreateSessionDates | null {
  if (!isRecord(value) || typeof value.startDate !== "string" || typeof value.endDate !== "string") return null;

  return { startDate: value.startDate, endDate: value.endDate };
}

/** The batch shape lists its periods under `sessions`; the legacy one dates its single session at the root. */
function isCreateSessionBatchInput(input: unknown): input is ICreateSessionBatchToolInput {
  return isRecord(input) && Array.isArray(input.sessions);
}

/** "du 10 octobre 2026 au 11 octobre 2026" — the ISO strings go to the formatter as they are. */
export function createSessionPeriodLabelOf(period: ICreateSessionDates, deps: ICopilotToolSummaryDeps): string {
  return `du ${deps.toDateLongDisplay(period.startDate)} au ${deps.toDateLongDisplay(period.endDate)}`;
}

/**
 * The periods a `createSession` call schedules, in the order the assistant proposed them. Read off
 * `unknown`: while the input streams in, it is a partial object with any field still missing.
 */
export function createSessionPeriodsOf(input: unknown): ICreateSessionDates[] {
  const candidates = isCreateSessionBatchInput(input) ? input.sessions : [input];

  return candidates.flatMap((candidate) => createSessionPeriodOf(candidate) ?? []);
}

/** An error does not prove nothing was written: the check to make before any retry. */
export const CREATE_SESSION_RETRY_ADVICE = "Vérifiez les sessions existantes dans Simbel avant de réessayer.";

const SESSION_BATCH_STATUSES = new Set<string>(Object.values(CopilotSessionBatchStatusEnum));

/** Mirrors the backend's own visibility check: rows plus both counts, under a known status. */
export function isCreateSessionBatchOutput(output: unknown): output is ICreateSessionBatchOutput {
  return (
    isRecord(output) &&
    Array.isArray(output.results) &&
    typeof output.createdCount === "number" &&
    typeof output.failedCount === "number" &&
    typeof output.status === "string" &&
    SESSION_BATCH_STATUSES.has(output.status)
  );
}

/** "3 sessions" / "1 session". */
function sessionCountLabelOf(count: number): string {
  return `${count} session${count > 1 ? "s" : ""}`;
}

function createSessionBatchHeadlineOf(status: CopilotSessionBatchStatusEnum, created: number, total: number): string {
  if (status === CopilotSessionBatchStatusEnum.FAILED) return "Aucune session n'a été créée.";
  if (status === CopilotSessionBatchStatusEnum.PARTIAL) {
    return `${sessionCountLabelOf(created)} sur ${total} créée${created > 1 ? "s" : ""}.`;
  }

  return created === 1 ? "Session créée." : `${sessionCountLabelOf(created)} créées.`;
}

/**
 * The backend runs the periods in the order proposed, but the card follows the proposal rather than
 * trusting that: each period takes the first answer still unclaimed for its dates, and whatever is
 * left — or everything, when the proposal is unknown — keeps the backend's order after them.
 */
function createSessionRowsInProposalOrder(
  results: unknown[],
  periods: ICreateSessionDates[],
): ICreateSessionRowResult[] {
  const unclaimed = results.filter(
    (row): row is ICreateSessionRowResult => isRecord(row) && createSessionPeriodOf(row) !== null,
  );
  const claimed: ICreateSessionRowResult[] = [];

  for (const period of periods) {
    const index = unclaimed.findIndex((row) => row.startDate === period.startDate && row.endDate === period.endDate);
    if (index !== -1) claimed.push(...unclaimed.splice(index, 1));
  }

  return [...claimed, ...unclaimed];
}

/**
 * The finished batch, one line per period. `null` for anything else — the legacy single-session
 * output keeps the card's usual footer. A partial batch is never worded as a success, and a failure
 * always asks for a check before a retry: once, by the rows when the backend explained every one of
 * them, by the card otherwise.
 */
export function createSessionBatchResultOf(
  output: unknown,
  input: unknown,
  deps: ICopilotToolSummaryDeps,
): ICreateSessionBatchResult | null {
  if (!isCreateSessionBatchOutput(output)) return null;

  const rows = createSessionRowsInProposalOrder(output.results, createSessionPeriodsOf(input)).map((row) => {
    const created = row.status === CopilotSessionBatchStatusEnum.CREATED;
    const message = typeof row.message === "string" && row.message.trim() ? row.message.trim() : null;

    return { label: createSessionPeriodLabelOf(row, deps), created, message: created ? null : message };
  });
  const failedRows = rows.filter((row) => !row.created);
  // One unexplained failure is enough for the card to say it: a failed row must not be left without the advice.
  const explained = failedRows.length > 0 && failedRows.every((row) => !!row.message);
  const { status, createdCount, failedCount } = output;

  return {
    status,
    // The counts the backend reported, not the rows painted: a row it could not date still happened.
    headline: createSessionBatchHeadlineOf(status, createdCount, createdCount + failedCount),
    advice: status === CopilotSessionBatchStatusEnum.CREATED || explained ? null : CREATE_SESSION_RETRY_ADVICE,
    rows,
  };
}

/**
 * A batch that threw leaves every period unknown, so the check before a retry applies to all of
 * them; the legacy single-session card keeps its usual failure wording.
 */
export function createSessionRetryAdviceOf(input: unknown): string | null {
  return isCreateSessionBatchInput(input) ? CREATE_SESSION_RETRY_ADVICE : null;
}

/** The headline glyph follows the batch's outcome: a check only when every session was created. */
export const CREATE_SESSION_BATCH_HEADLINE_ICONS: Record<CopilotSessionBatchStatusEnum, string> = {
  [CopilotSessionBatchStatusEnum.CREATED]: "check",
  [CopilotSessionBatchStatusEnum.PARTIAL]: "warning_error",
  [CopilotSessionBatchStatusEnum.FAILED]: "warning_error",
};

/** The model is still filling the action object in: nothing to approve yet. */
export function isCopilotToolPending(state: CopilotToolStateEnum): boolean {
  return state === CopilotToolStateEnum.INPUT_STREAMING || state === CopilotToolStateEnum.INPUT_AVAILABLE;
}

/** Approved and running server-side. */
export function isCopilotToolRunning(state: CopilotToolStateEnum): boolean {
  return state === CopilotToolStateEnum.APPROVAL_RESPONDED;
}
